/**
 * TEMP-H-008: Orientation Overrides Editor
 * 
 * Post-launch editor for per-scene orientation overrides.
 * Reads current overrides and shows effective orientation with fallback to default.
 * Updates via StageSettings API.
 */

import React, { useState, useEffect } from 'react';
import { Monitor, Save, RotateCcw, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { API_BASE_URL } from '@/App';

interface OrientationOverride {
  sceneId: string;
  effectiveOrientation: '9:16' | '16:9' | '1:1';
  override?: '9:16' | '16:9' | '1:1';
  isDefault: boolean;
}

interface OrientationOverridesEditorProps {
  executionId: string;
  stage: 'frames' | 'videos';
  orientationDefault?: '9:16' | '16:9' | '1:1';
  className?: string;
  onSave?: (overrides: Record<string, string>) => void;
}

type OrientationType = '9:16' | '16:9' | '1:1';

const ORIENTATION_OPTIONS: Array<{ value: OrientationType; label: string; description: string }> = [
  { value: '9:16', label: '9:16', description: 'Vertical (Mobile)' },
  { value: '16:9', label: '16:9', description: 'Horizontal (Desktop)' },  
  { value: '1:1', label: '1:1', description: 'Square (Social)' }
];

export const OrientationOverridesEditor: React.FC<OrientationOverridesEditorProps> = ({
  executionId,
  stage,
  orientationDefault = '9:16',
  className = '',
  onSave
}) => {
  const [orientations, setOrientations] = useState<OrientationOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current orientation overrides
  const fetchOrientationOverrides = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get stage settings which includes orientation overrides
      const response = await fetch(`${API_BASE_URL}/api/stage-thresholds/${executionId}/${stage}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch orientation overrides: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch orientation overrides');
      }

      const stageSettings = result.data;
      const overrideKey = stage === 'frames' ? 'frameOrientationOverrides' : 'videoOrientationOverrides';
      const overrides = stageSettings[overrideKey] || {};

      // TODO: Replace with real scene data from execution
      // Option A: GET /api/executions/:id/stages/:stage/iterations (pending E7)  
      // Option B: Extend stage settings endpoint to return scene list
      // For now, using common scene structure as placeholder
      const defaultScenes = [
        'intro_scene', 'main_concept', 'example_1', 'example_2', 'conclusion'
      ];

      const orientationData = defaultScenes.map((sceneId) => ({
        sceneId,
        override: overrides[sceneId] as OrientationType | undefined,
        effectiveOrientation: (overrides[sceneId] as OrientationType) || orientationDefault,
        isDefault: !overrides[sceneId]
      }));

      setOrientations(orientationData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching orientation overrides:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrientationOverrides();
  }, [executionId, stage]);

  const handleOrientationChange = (sceneId: string, newOrientation: OrientationType | 'default') => {
    setOrientations(prev => prev.map(item => {
      if (item.sceneId === sceneId) {
        const isDefault = newOrientation === 'default';
        const effectiveOrientation = isDefault ? orientationDefault : (newOrientation as OrientationType);
        
        return {
          ...item,
          override: isDefault ? undefined : (newOrientation as OrientationType),
          effectiveOrientation,
          isDefault
        };
      }
      return item;
    }));
    
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Build overrides object from current state
      const overrides: Record<string, string> = {};
      orientations.forEach(item => {
        if (item.override) {
          overrides[item.sceneId] = item.override;
        }
      });

      const overrideKey = stage === 'frames' ? 'frameOrientationOverrides' : 'videoOrientationOverrides';
      const payload = {
        [overrideKey]: overrides,
        updatedBy: 'user' // In real implementation, this would be the current user ID
      };

      const response = await fetch(`${API_BASE_URL}/api/stage-thresholds/${executionId}/${stage}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to save orientation overrides: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to save orientation overrides');
      }

      setHasChanges(false);
      onSave?.(overrides);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error saving orientation overrides:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchOrientationOverrides();
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <Monitor className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">
            {stage === 'frames' ? 'Frame' : 'Video'} Orientation Overrides
          </h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Loading orientation settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Monitor className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {stage === 'frames' ? 'Frame' : 'Video'} Orientation Overrides
            </h3>
            <p className="text-sm text-gray-500">
              Set per-scene orientation overrides. Default: {orientationDefault} ({ORIENTATION_OPTIONS.find(opt => opt.value === orientationDefault)?.description})
            </p>
          </div>
        </div>
        
        {hasChanges && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              disabled={saving}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {!hasChanges && !loading && orientations.length > 0 && !error && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-green-800">Orientation overrides are up to date</div>
          </div>
        </div>
      )}

      {/* Orientation Overrides List */}
      {orientations.length > 0 ? (
        <div className="space-y-4">
          {orientations.map((item) => (
            <div 
              key={item.sceneId} 
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-medium text-gray-900 capitalize">
                    {item.sceneId.replace(/_/g, ' ')}
                  </h4>
                  {item.isDefault && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      <Info className="w-3 h-3" />
                      Default
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  Current: {item.effectiveOrientation} ({ORIENTATION_OPTIONS.find(opt => opt.value === item.effectiveOrientation)?.description})
                </div>
              </div>
              
              <div className="flex-shrink-0 ml-4">
                <select
                  value={item.override || 'default'}
                  onChange={(e) => handleOrientationChange(item.sceneId, e.target.value as OrientationType | 'default')}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  aria-label={`Orientation override for ${item.sceneId}`}
                >
                  <option value="default">Use Default ({orientationDefault})</option>
                  {ORIENTATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} - {option.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No scenes available for orientation overrides
        </div>
      )}

      {/* Info Panel */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">About Orientation Overrides</p>
            <ul className="space-y-1 text-blue-700">
              <li>• Per-scene overrides take precedence over the execution default orientation</li>
              <li>• Changes apply to future processing of these scenes</li>
              <li>• Scenes without overrides will use the default orientation ({orientationDefault})</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrientationOverridesEditor;